import React, { SFC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Button,
    Dialog,
    DialogTitle,
    makeStyles,
    DialogContent,
    DialogActions,
    Grid,
    Typography,
    Checkbox,
    Tooltip,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { Form } from 'react-final-form'
import { Location } from '../../../shared/types/location'
import { DialogProgress } from '../../../shared/components/dialogProgress'
import { DialogConfirmButton } from '../../../shared/components/dialogConfirmButton'
import { fetchMarkets } from '../../../redux/actions/dashboard'
import { config } from '../../../config'
import LoadBalancerEditor from './LoadBalancerEditor'
import { LoadBalancer } from '../../../shared/types/flexiCharging'
import { Charger } from '../../../shared/types/charger'
import { v4 as uuidv4 } from 'uuid';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { editFlexiCharging } from '../../../redux/actions/locations'


const mainTenantId = config.mainTenantId
const chargeTenantId = config.chargeTenantId

const useStyles = makeStyles((theme) => ({
    dialog: {

        '& .MuiDialog-paper': {
            width: '650px',
            maxWidth: 'none',
            height: '80vh',
            maxHeight: 'none',
            display: 'flex',
            flexDirection: 'column',
        },
        '& .MuiFilledInput-root': {
            borderRadius: theme.spacing(0.5),
        },
        '& .MuiFilledInput-root::before': {
            border: 0,
        },
        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
        },
        '& input[type=number]': {
            '-moz-appearance': 'textfield',
        },
        '& .DraftEditor-editorContainer': {
            maxHeight: 'calc(100vh - 272px)',
        },
    },
    form: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'hidden',
        flex: 1,
    },

    disabledContainer: {
        opacity: 0.5,
        pointerEvents: 'none',
        userSelect: 'none',
    },

    dialogTitle: {
        paddingBottom: theme.spacing(1),
    },

    dialogContent: {
        flexGrow: 1,
        overflowY: 'auto',
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
    },

    selectContainer: {
        marginBottom: theme.spacing(3),
    },
    dialogActions: {
        padding: theme.spacing(1, 3, 3, 3),
        flexShrink: 0,
    },
    fullWidth: {
        width: '100%',
    },
    checkbox: {
        color: '#6100ED',
        '&$checked': {
            color: '#6100ED',
        },
        '&$disabled': {
            color: '#A0A0A0',
        },
        '&$checked$disabled': {
            color: '#A0A0A0',
        },
        marginBottom: '4px',
        padding: '0 8px 0 0',
    },
    checked: {},
    disabled: {},
}));

interface FlexiChargingSetupDialogProps {
    open: boolean
    onClose(location?: Location): void
    initialModel?: Location
}

const FlexiChargingSetupDialog: SFC<FlexiChargingSetupDialogProps> = ({ open, onClose, initialModel }) => {
    const classes = useStyles()
    const { t } = useTranslation()

    const dispatch = useDispatch()

    const tenantId = useSelector((state: any) => state.login.value.user.tenantId)
    const marketsState = useSelector((state: any) => state.markets)

    const [flexiCharging, setFlexiCharging] = useState<boolean>(initialModel?.flexiCharging || false)
    const [beforeSubmit, setBeforeSubmit] = useState<boolean>(true)
    const [, setTenant] = useState<string>('')
    const [market, setMarket] = useState<string>('')

    const [loadBalancers, setLoadBalancers] = useState<LoadBalancer[]>(initialModel?.loadBalancers || []);
    const [chargers, setChargers] = useState<Charger[]>(initialModel?.chargers || []);

    const onDialogClose = (location?: Location) => {
        onClose(location)
    }

    const handleCreateLb = () => {
        const newLb: LoadBalancer = {
            id: uuidv4(),
            locationId: initialModel?.id || '',
            name: 'New LB',
            capacityKw: 0,
            parentLbId: null,
            active: true,
        };
        setLoadBalancers(prev => {
            const newArray = [...prev, newLb];
            return newArray;
        });
        return newLb.id;
    };

    const handleEditLb = (type: "lb" | "charger", id: string, changes: any) => {

        if (type === "lb") {
            // UPDATE LOAD BALANCER
            setLoadBalancers(prev =>
                prev.map(lb =>
                    lb.id === id ? { ...lb, ...changes } : lb
                )
            );
            return;
        }

        if (type === "charger") {
            // UPDATE CHARGER
            setChargers(prev =>
                prev.map(ch =>
                    ch.id === id ? { ...ch, ...changes } : ch
                )
            );
            return;
        }
    };

    const handleDeleteLb = (lbId: string) => {
        // Find all nested children LBs recursively
        const allChildIds = (id: string): string[] => {
            const children = loadBalancers.filter(lb => lb.parentLbId === id);
            return children.reduce<string[]>(
                (acc, child) => [...acc, child.id, ...allChildIds(child.id)],
                []
            );
        };

        const idsToDelete = [lbId, ...allChildIds(lbId)]; // all LBs to remove

        // 1) Remove the LBs
        setLoadBalancers(prev =>
            prev.filter(lb => !idsToDelete.includes(lb.id))
        );

        // 2) Unassign chargers that belonged to deleted LBs
        setChargers(prev =>
            prev.map(c =>
                c.loadBalancerId && idsToDelete.includes(c.loadBalancerId)
                    ? { ...c, loadBalancerId: null }
                    : c
            )
        );
    };

    const handleFlexiChargingChange = () => {
        setFlexiCharging(!flexiCharging)
    }

    const handleMarketChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setMarket(event.target.value as string)
    }

    const handleSubmit = async () => {
        if (!initialModel?.id) {
            console.error("Location ID is missing");
            return;
        }

        try {
            const result: any = await dispatch(
                editFlexiCharging(
                    initialModel.id, // guaranteed string now
                    chargers,
                    loadBalancers,
                    market,
                    flexiCharging
                )
            );

            if (result) {
                onDialogClose(result);
            }
        } catch (err) {
            console.error(err);
        }
    };

    // load all general data
    useEffect(() => {
        dispatch(fetchMarkets())
    }, [dispatch, tenantId])

    useEffect(() => {
        if (!open) return;
        setFlexiCharging(initialModel?.flexiCharging || false);
        setTenant(initialModel?.tenantId || '');
        setMarket(initialModel?.marketId || (marketsState?.value?.[0]?.id ?? ''));
        setLoadBalancers(initialModel?.loadBalancers || []);
        setChargers(initialModel?.chargers || []);
        setBeforeSubmit(true);
    }, [open, initialModel, marketsState]);

    useEffect(() => {
        if (initialModel) {
            if (initialModel?.tenantId) {
                // editing existing → use initialModel
                setTenant(initialModel.tenantId as string);
            } else {
                // creating new → apply custom rule
                if (tenantId === mainTenantId) {
                    setTenant(chargeTenantId);
                } else {
                    setTenant(tenantId);
                }
            }
            if (initialModel?.marketId) {
                // editing existing → use initialModel
                setMarket(initialModel.marketId as string);
            } else if (marketsState && marketsState?.value?.length > 0) {
                setMarket(marketsState.value[0].id as string);
            }
            setFlexiCharging(initialModel.flexiCharging)
        }
    }, [open, initialModel, marketsState, tenantId])

    return (
        <Dialog className={classes.dialog} open={open}>
            <DialogProgress stateSelector={(state) => state.locations.edit} />
            <DialogTitle className={classes.dialogTitle}>{"Flexi Charging Setup"}</DialogTitle>

            <Form
                onSubmit={handleSubmit}
                initialValues={initialModel}
                validate={() => {
                    const errors: Record<string, any> = {};

                    loadBalancers.forEach(lb => {
                        if (!lb.name?.trim()) {
                            errors[lb.id] = { name: 'Name is required' };
                        }
                        if (lb.capacityKw == null || lb.capacityKw <= 0) {
                            errors[lb.id] = { ...errors[lb.id], capacityKw: 'Capacity is required' };
                        }
                    });

                    const validateHierarchy = (parentId: string | null, parentCapacity: number) => {
                        // Nájdeme všetky priame deti tohto rodiča
                        const children = loadBalancers.filter(lb => lb.parentLbId === parentId);

                        // Vypočítame sumu kapacít detí
                        const childrenTotalCapacity = children.reduce((sum, child) => sum + (Number(child.capacityKw) || 0), 0);

                        // Ak suma detí prekročí kapacitu rodiča, označíme všetky tieto deti chybou
                        if (parentId !== null && childrenTotalCapacity > parentCapacity) {
                            children.forEach(child => {
                                errors[child.id] = {
                                    ...errors[child.id],
                                    capacityKw: `Sum of siblings (${childrenTotalCapacity}kW) exceeds parent capacity (${parentCapacity}kW)`
                                };
                            });
                        }

                        // Rekurzia: Skontrolujeme deti týchto detí
                        children.forEach(child => validateHierarchy(child.id, child.capacityKw));
                    };

                    // Spustíme validáciu od koreňových elementov
                    // Predpokladáme, že "hlavný prívod" (root) je limitovaný nejakou externou hodnotou alebo 
                    // proste len kontrolujeme úrovne pod ním.
                    const roots = loadBalancers.filter(lb => !lb.parentLbId);
                    roots.forEach(root => validateHierarchy(root.id, root.capacityKw));

                    return { loadBalancers: errors };
                }}


                render={({ handleSubmit, form, values, errors }) => (
                    <form onSubmit={handleSubmit} noValidate className={classes.form}>
                        <DialogContent className={classes.dialogContent}>
                            <Grid item xs={12}>
                                <Typography variant="caption" style={{ fontSize: '18px', display: 'flex', alignItems: 'center' }}>
                                    <Checkbox
                                        name="flexiCharging"
                                        checked={flexiCharging}
                                        onChange={handleFlexiChargingChange}
                                        disabled={tenantId !== mainTenantId}
                                        classes={{
                                            root: classes.checkbox,
                                            checked: classes.checked,
                                            disabled: classes.disabled,
                                        }}
                                    />
                                    <span style={{ color: tenantId === mainTenantId ? '#000000' : '#A0A0A0' }}>
                                        {t('locations.flexiCharging')}
                                    </span>

                                    <Tooltip title='Flexi charging feature may be enabled/disabled only by SuperAdmin, please contact support if you want to change it.' arrow>
                                        <span style={{ display: 'inline-block', verticalAlign: 'middle', cursor: 'pointer' }}>
                                            <IconButton
                                                size="small"
                                                style={{
                                                    marginLeft: 4,
                                                    bottom: 1,
                                                    pointerEvents: tenantId === mainTenantId ? 'auto' : 'none',
                                                }}
                                            >
                                                <HelpOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Typography>
                            </Grid>
                            <div className={!flexiCharging ? classes.disabledContainer : ''}>
                                <Grid item xs={12} className={classes.selectContainer}>
                                    <FormControl required variant="filled" fullWidth>
                                        <InputLabel >{t('locations.market')}</InputLabel>
                                        <Select value={market} onChange={handleMarketChange}>
                                            {marketsState.value.map((item: any) => (
                                                <MenuItem key={item.id} value={item.id}>
                                                    {item.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <LoadBalancerEditor
                                    loadBalancers={loadBalancers}
                                    chargers={chargers}
                                    onEdit={handleEditLb}
                                    onCreate={handleCreateLb}
                                    onDelete={handleDeleteLb}
                                    errors={errors.loadBalancers} // ✅ directly from form errors
                                />
                            </div>
                        </DialogContent>
                        <DialogActions className={classes.dialogActions}>
                            <Button
                                onClick={() => {
                                    onDialogClose()
                                }}
                                color="primary"
                            >
                                {t('common.cancel')}
                            </Button>
                            <DialogConfirmButton
                                label={t('common.change')}
                                stateSelector={(state) => state.locations.edit}
                                onClick={() => {
                                    // setValidationActive(true);
                                    if (beforeSubmit) {
                                        setBeforeSubmit(false)
                                    }
                                }}
                            />
                        </DialogActions>
                    </form>
                )}
            />
        </Dialog>
    )
}

export default FlexiChargingSetupDialog
